<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import ReaderSidebarItemComponent from './ReaderSidebarItem.svelte';
  import type { ReaderSidebarItem } from '../../types/reader.interface';
  import InlineSvg from '../elements/InlineSvg.svelte';
  import { randomBetween } from '../../lib/utils';
  import { fade } from 'svelte/transition';
  import type { ReaderListCategory } from '../../lib/reader-list';
  import { favoriteLibraryNames } from '../../store';
  import Chevron from '../elements/Chevron.svelte';

  export let items: ReaderListCategory[] = [];
  export let activeItem: any = items[0];
  export let className: 'categories' | 'sources';
  export let featured: ReaderSidebarItem[] = null;
  export let listTitle = activeItem?.category || '';
  export let collapse = false;
  export let itemSaved = false;
  export let isMobile = false;
  export let isSubMenu = false;

  let scrolled = false;
  let rsScroll;

  function onScroll() {
    scrolled = rs.scrollTop ? true : false;
    rsScroll = rs?.scrollTop;
  }

  const dispatch = createEventDispatcher();

  let rs: HTMLDivElement;

  $: onItemsChange(items);

  function onItemsChange(_: any[]) {
    if (!rs) return;

    rs.scrollTop = 0;
  }

  function getPlaceholderTitleStyle() {
    return `margin-right: ${randomBetween(20, 50)}%;`;
  }
</script>

<div
  class="reader-sidebar {className}"
  class:collapse
  bind:this={rs}
  on:scroll={onScroll}
>
  {#if className === 'categories'}
    <div class="logo">
      <a href="/">
        {#if !collapse}
          <h1 in:fade>
            <InlineSvg src="/img/the-stocks-v3.svg" />
          </h1>
        {:else}
          <h1 in:fade>
            <InlineSvg src="/favicon.svg" width="32px" height="32px" />
          </h1>
        {/if}
      </a>
      <div class="shadow" class:active={scrolled} />
    </div>
  {:else if isMobile && isSubMenu}
    <div
      class="sub-menu-header"
      on:click={() => {
        isSubMenu = !isSubMenu;
      }}
    >
      <span class="arrow">
        <Chevron direction="left" />
      </span>
      <span>{listTitle}</span>
      <div class="shadow" class:active={scrolled} />
    </div>
  {/if}
  <ul class="rs-list">
    {#if featured}
      {#if !isMobile}
        <h4 class="first" class:scale={collapse}>Featured</h4>
      {/if}
      {#each featured as featuredItem}
        <ReaderSidebarItemComponent
          item={featuredItem}
          featuredItem={true}
          {collapse}
          {rsScroll}
          {className}
          {isMobile}
          active={activeItem === featuredItem}
          on:saved
          on:click={() => dispatch('select', featuredItem)}
        />
      {/each}
    {:else if listTitle && !isMobile}
      <h4 class="first list-title" class:scale={collapse}>{listTitle}</h4>
    {/if}
    {#if !items.length}
      {#each Array(9) as _}
        <li class="rsli-placeholder">
          <span class="rsli-icon" />
          <span class="rsli-title" style={getPlaceholderTitleStyle()} />
        </li>
      {/each}
    {/if}
    {#if listTitle === 'Home' && items.length && !isMobile}
      <h4 class:first={!featured} class:scale={collapse}>Recently Added</h4>
    {/if}
    {#each items as item (item.id || item.name)}
      {#if !JSON.stringify(featured).includes(item.name)}
        <ReaderSidebarItemComponent
          {item}
          {rsScroll}
          {itemSaved}
          {collapse}
          {className}
          {isMobile}
          itemCount={item.sources?.length}
          active={activeItem === item}
          on:saved
          on:click={() => dispatch('select', item)}
        />
        {#if item.name === 'Saved Items' && !isMobile}
          <span class="seperator"> Categories </span>
        {/if}
      {/if}
    {/each}
  </ul>
</div>

<style lang="scss">
  @import '../../styles/mixins';

  .reader-sidebar {
    height: 100%;
    flex: 0 0 208px;
    overflow: overlay;
    overflow-x: hidden;
    box-shadow: inset -1px 0px 0px #f0f2f4;
    padding-bottom: 32px;
    position: relative;
    transition: all 0.1s ease-in;

    &.article {
      flex: 0 0 320px;
    }

    &.collapse {
      flex: 0 0 64px;
      overflow-x: hidden !important;
    }

    .seperator {
      box-sizing: border-box;
      display: inline-block;
      width: 100%;
      margin: 0 16px;
      border-style: solid;
      border-image: linear-gradient(to right, rgba(255, 255, 255, 0.15), rgba(0, 0, 0, 0))
        1 100%;
      border-image-slice: 1;
      border-image-width: 1px 0 0 0;
      @include font(Semibold);
      font-size: 10px;
      line-height: 140%;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--color-blue-gray--200);
      padding: 18px 8px 8px;
      margin-top: 1px;
      transition: all 0.1s ease-in;
    }

    &.collapse .seperator {
      font-size: 8px;
      margin-left: -5px;
    }
  }

  .rs-list {
    h4 {
      @include font(Semibold);
      padding-left: 24px;
      font-size: 10px;
      line-height: 14px;
      letter-spacing: 0.05em;
      color: var(--color-blue-gray--200);
      margin-top: 16px;
      margin-bottom: 8px;
      text-transform: uppercase;
      white-space: nowrap;
      user-select: none;

      &.first {
        margin-top: 32px;
        margin-bottom: 14px;

        &.list-title {
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }

      &.scale {
        font-size: 8px;
        margin-left: -13px;
        text-align: center;
        max-width: 13ch;
        overflow: hidden;
      }
    }
  }
  .rsli-placeholder {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    height: 40px;
    padding-left: 24px;

    .article & {
      height: 60px;
      flex-direction: column-reverse;
      align-items: flex-start;
      justify-content: flex-end;
      padding: 12px 24px;

      .rsli-icon {
        flex: 0 0 9px;
        width: 109px;
        margin-top: 6px;
      }

      .rsli-title {
        flex: 0 0 15px;
        width: 100%;
      }
    }

    .rsli-icon {
      display: inline-block;
      flex: 0 0 16px;
      width: 16px;
      height: 16px;
      margin-right: 12px;
      background-color: var(--color-blue-gray--100);
      border-radius: 100px;
    }

    .rsli-title {
      flex: 1;
      height: 10px;
      background-color: var(--color-blue-gray--100);
      border-radius: 100px;
    }
  }

  .collapse .rsli-placeholder {
    .rsli-title {
      display: none;
    }
  }

  .logo {
    position: sticky;
    top: 0;
    width: 208px;
    height: 60px;
    background-color: var(--color-bg--lightest);
    box-shadow: inset -1px 0px 0px #f0f2f4;
    z-index: 2;
    a,
    h1 {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }
  }

  :not(.collapse) .logo {
    :global(svg path) {
      fill: var(--color-blue-gray--900) !important;
    }
  }

  .shadow {
    position: absolute;
    top: 60px;
    width: 100%;
    height: 20px;
    pointer-events: none;

    &.active {
      background-image: linear-gradient(rgba(0, 0, 0, 0.06), rgba(0, 0, 0, 0));
    }
  }

  .collapse .logo a {
    justify-content: flex-start;
    margin-left: 16px;
  }
  .sub-menu-header {
    position: sticky;
    top: 0;
    height: 60px;
    display: flex;
    align-items: center;
    @include font(Semibold);
    font-size: 20px;
    line-height: 140%;
    color: var(--color-blue-gray--800);
    background-color: var(--color-bg--lightest);
    cursor: pointer;
    user-select: none;
    z-index: 1;

    .arrow {
      margin-left: 24px;
      margin-right: 12px;
      margin-top: -4px;
      --svg-stroke: var(--color-blue-gray--800);
    }
  }
  :global(.dark-mode) {
    .logo {
      background-color: var(--color-blue-gray--900) !important;
      box-shadow: inset -1px 0px 0px 0px rgba(237, 237, 237, 0.05);
    }

    :not(.collapse) .logo {
      :global(svg path) {
        fill: var(--color-bg--lightest) !important;
      }
    }

    .reader-sidebar {
      box-shadow: inset -1px 0px 0px rgba(237, 237, 237, 0.05);
    }
    .shadow {
      &.active {
        background-image: linear-gradient(
          rgba(255, 255, 255, 0.06),
          rgba(255, 255, 255, 0)
        );
      }

      @media screen and (max-width: 600px) {
        &.active {
          background-image: linear-gradient(rgba(21, 25, 34, 0.8), rgba(21, 25, 34, 0));
        }
      }
    }

    .rs-list h4 {
      color: var(--color-blue-gray--300);
    }

    .rsli-placeholder {
      .rsli-icon {
        background-color: var(--color-blue-gray--700);
      }

      .rsli-title {
        background-color: var(--color-blue-gray--500);
      }
    }

    .sub-menu-header {
      color: var(--color-blue-gray--50);
      background-color: var(--color-blue-gray--900);

      .arrow {
        margin-left: 24px;
        margin-right: 12px;
        --svg-stroke: var(--color-blue-gray--50);
      }
    }
  }

  @media screen and (max-width: 600px) {
    .reader-sidebar {
      flex: 0 0 100%;

      .logo {
        display: flex;
        justify-content: space-between;
        width: 100%;

        h1 {
          margin-left: 24px;
        }
      }
    }
  }
</style>
